// customization:
// added table container to allow scroll horizontally

/**
 * Slate Table block's View component.
 * @module volto-slate/blocks/Table/View
 */

import React, { useState, useMemo, useLayoutEffect, useRef } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import PropTypes from 'prop-types';
import { Table, Button } from 'semantic-ui-react';
import { Icon } from '@redturtle/iovisit-theme/components';
import map from 'lodash/map';
import {
  serializeNodes,
  serializeNodesToText,
} from '@plone/volto-slate/editor/render';
import { Node } from 'slate';

// TODO: loading LESS files with `volto-slate/...` paths does not work currently
import '@plone/volto-slate/editor/plugins/Table/less/public.less';

/**
 * Slate Table block's View class.
 * @class View
 * @extends Component
 * @param {object} data The table data to render as a table.
 */
const View = ({ data }) => {
  const intl = useIntl();
  const [state, setState] = useState({
    column: null,
    direction: null,
  });
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;

    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
  };

  const headers = useMemo(() => {
    return data.table.rows?.[0]?.cells;
  }, [data.table.rows]);

  const rows = useMemo(() => {
    const items = [];
    if (!data.table.rows) return [];
    data.table.rows.forEach((row, index) => {
      if (index > 0) {
        items[index] = [];
        row.cells.forEach((cell, cellIndex) => {
          items[index][cellIndex] = {
            ...cell,
            value:
              cell.value && Node.string({ children: cell.value }).length > 0
                ? serializeNodes(cell.value)
                : '\u00A0',
            valueText:
              cell.value && Node.string({ children: cell.value }).length > 0
                ? serializeNodesToText(cell.value)
                : '\u00A0',
          };
        });
      }
    });
    return items;
  }, [data.table.rows]);

  const sortedRows = useMemo(() => {
    if (state.column === null) return Object.keys(rows);
    return Object.keys(rows).sort((a, b) => {
      const a_text = rows[a][state.column].valueText;
      const b_text = rows[b][state.column].valueText;
      if (state.direction === 'ascending' ? a_text < b_text : a_text > b_text) {
        return -1;
      }
      if (state.direction === 'ascending' ? a_text > b_text : a_text < b_text) {
        return 1;
      }
      return 0;
    });
  }, [state, rows]);

  const handleSort = (index) => {
    if (!data.table.sortable) return;
    setState({
      column: index,
      direction:
        state.column !== index
          ? 'ascending'
          : state.direction === 'ascending'
            ? 'descending'
            : 'ascending',
    });
  };

  // buttons scroll
  const scroll = (direction) => {
    if (scrollRef.current) {
      const amount = 250; // px da scorrere per click
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -amount : amount,
        behavior: 'smooth',
      });
    }
  };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollButtons();

    el.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);

    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [data]);

  return (
    <div className="table block view-mode">
      {(canScrollRight || canScrollLeft) && (
        <div className="scroll-buttons">
          <Button
            icon
            size="small"
            className="arrow-image arrow-left ps-0"
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            aria-hidden="true"
            tabindex="-1"
          >
            <Icon prefix="fa" icon="chevron-left" className="icon" />
            {intl.formatMessage(messages.label_scroll_left_button)}
          </Button>
          <Button
            icon
            size="small"
            className="pe-0"
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            aria-hidden="true"
            tabindex="-1"
          >
            {intl.formatMessage(messages.label_scroll_right_button)}
            <Icon prefix="fa" icon="chevron-right" className="icon" />
          </Button>
        </div>
      )}
      {data && data.table && (
        <div className="table-container" ref={scrollRef}>
          <Table
            fixed={data.table.fixed}
            compact={data.table.compact}
            basic={data.table.basic ? 'very' : false}
            celled={data.table.celled}
            inverted={data.table.inverted}
            striped={data.table.striped}
            sortable={data.table.sortable}
            className="slate-table-block"
            unstackable
          >
            {!data.table.hideHeaders ? (
              <Table.Header>
                <Table.Row>
                  {headers.map((cell, index) => (
                    <Table.HeaderCell
                      key={cell.key}
                      textAlign="left"
                      verticalAlign="middle"
                      tabIndex={data.table.sortable ? '0' : '-1'}
                      sorted={state.column === index ? state.direction : null}
                      onClick={() => {
                        handleSort(index);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSort(index);
                        }
                      }}
                      aria-sort={
                        state.column === index ? state.direction : 'none'
                      }
                    >
                      {cell.value &&
                      Node.string({ children: cell.value }).length > 0
                        ? serializeNodes(cell.value)
                        : '\u00A0'}
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
            ) : (
              ''
            )}
            <Table.Body>
              {map(sortedRows, (row) => (
                <Table.Row key={row}>
                  {map(rows[row], (cell) => (
                    <Table.Cell
                      key={cell.key}
                      textAlign="left"
                      verticalAlign="middle"
                    >
                      {cell.value}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </div>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
View.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default View;

const messages = defineMessages({
  label_scroll_left_button: {
    id: 'search_table_at_label_scroll_left_button',
    defaultMessage: 'Scorri a sinistra',
  },
  label_scroll_right_button: {
    id: 'search_table_at_label_scroll_right_button',
    defaultMessage: 'Scorri a destra',
  },
});
